import { useState, useCallback } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Save,
  SkipForward,
  CheckCircle,
  ArrowLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LoadingSpinner, useToast } from '@/components/common';
import {
  useOnboardingQuestions,
  useOnboardingProgress,
  useSubmitAnswer,
} from '@/hooks/useOnboarding';

export interface OnboardingWizardProps {
  firmId: number;
  onBack: () => void;
}

const PAGE_SIZE = 1;

export function OnboardingWizard({ firmId, onBack }: OnboardingWizardProps) {
  const { success: showSuccess, error: showError } = useToast();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [answerText, setAnswerText] = useState('');
  const [skippedIds, setSkippedIds] = useState<Set<number>>(new Set());

  // Fetch questions
  const { data: questionsData, isLoading: isLoadingQuestions } =
    useOnboardingQuestions({
      firm_id: firmId,
      category: selectedCategory || undefined,
      offset: currentIndex,
      limit: PAGE_SIZE,
    });

  // Fetch progress
  const { data: progress } = useOnboardingProgress(firmId);

  // Submit mutation
  const submitAnswer = useSubmitAnswer();

  const currentQuestion = questionsData?.questions?.[0];
  const totalQuestions = questionsData?.total ?? 0;

  // Get categories from progress data
  const categories = progress?.by_category
    ? Object.keys(progress.by_category).sort()
    : [];

  const handleSubmitAnswer = useCallback(async () => {
    if (!currentQuestion || !answerText.trim()) return;

    if (answerText.trim().length < 50) {
      showError('Answer Too Short', 'Answer must be at least 50 characters.');
      return;
    }

    try {
      await submitAnswer.mutateAsync({
        firm_id: firmId,
        canonical_question_id: currentQuestion.id,
        answer_text: answerText.trim(),
      });
      showSuccess('Answer Saved', 'Your answer has been saved successfully.');
      setAnswerText('');
      // Move to next question
      if (currentIndex < totalQuestions - 1) {
        setCurrentIndex((prev) => prev + 1);
      }
    } catch {
      showError('Error', 'Failed to save the answer. Please try again.');
    }
  }, [
    currentQuestion,
    answerText,
    firmId,
    currentIndex,
    totalQuestions,
    submitAnswer,
    showSuccess,
    showError,
  ]);

  const handleSkip = useCallback(() => {
    if (currentQuestion) {
      setSkippedIds((prev) => new Set([...prev, currentQuestion.id]));
    }
    setAnswerText('');
    if (currentIndex < totalQuestions - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  }, [currentQuestion, currentIndex, totalQuestions]);

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      setAnswerText('');
    }
  }, [currentIndex]);

  const handleNext = useCallback(() => {
    if (currentIndex < totalQuestions - 1) {
      setCurrentIndex((prev) => prev + 1);
      setAnswerText('');
    }
  }, [currentIndex, totalQuestions]);

  const handleCategoryChange = useCallback((category: string | null) => {
    setSelectedCategory(category);
    setCurrentIndex(0);
    setAnswerText('');
  }, []);

  return (
    <div className="space-y-4">
      {/* Header with back button and progress */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Overview
        </Button>
        {progress && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {progress.answered} of {progress.total} answered
            </span>
            <div className="w-32 bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full"
                style={{ width: `${Math.min(progress.percent, 100)}%` }}
              />
            </div>
            <span className="text-sm font-medium">{progress.percent}%</span>
          </div>
        )}
      </div>

      {/* Category filter tabs */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={selectedCategory === null ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => handleCategoryChange(null)}
        >
          All Categories
        </Button>
        {categories.map((cat) => (
          <Button
            key={cat}
            variant={selectedCategory === cat ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => handleCategoryChange(cat)}
          >
            {cat}
            {progress?.by_category[cat] && (
              <span className="ml-1 text-xs text-muted-foreground">
                ({progress.by_category[cat].answered}/{progress.by_category[cat].total})
              </span>
            )}
          </Button>
        ))}
      </div>

      {/* Question card */}
      {isLoadingQuestions ? (
        <Card>
          <CardContent className="flex items-center justify-center py-16">
            <LoadingSpinner />
          </CardContent>
        </Card>
      ) : totalQuestions === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">All Done!</h3>
            <p className="text-muted-foreground">
              {selectedCategory
                ? `All questions in "${selectedCategory}" have been answered.`
                : 'All onboarding questions have been answered.'}
            </p>
          </CardContent>
        </Card>
      ) : currentQuestion ? (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                Question {currentIndex + 1} of {totalQuestions}
              </CardTitle>
              {currentQuestion.category && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                  {currentQuestion.category}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Question text */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-base leading-relaxed">
                {currentQuestion.question_text}
              </p>
            </div>

            {/* Answer textarea */}
            <div>
              <label
                htmlFor="answer-input"
                className="block text-sm font-medium mb-2"
              >
                Your Answer
              </label>
              <textarea
                id="answer-input"
                value={answerText}
                onChange={(e) => setAnswerText(e.target.value)}
                placeholder="Type your answer here (minimum 50 characters)..."
                className="w-full min-h-[200px] p-3 border rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex items-center justify-between mt-1">
                <span
                  className={`text-xs ${
                    answerText.length > 0 && answerText.length < 50
                      ? 'text-red-500'
                      : 'text-muted-foreground'
                  }`}
                >
                  {answerText.length} characters
                  {answerText.length > 0 && answerText.length < 50
                    ? ` (minimum 50)`
                    : ''}
                </span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentIndex === 0}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={handleNext}
                  disabled={currentIndex >= totalQuestions - 1}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={handleSkip}>
                  <SkipForward className="h-4 w-4 mr-1" />
                  Skip
                </Button>
                <Button
                  onClick={handleSubmitAnswer}
                  disabled={
                    !answerText.trim() ||
                    answerText.trim().length < 50 ||
                    submitAnswer.isPending
                  }
                >
                  {submitAnswer.isPending ? (
                    <LoadingSpinner />
                  ) : (
                    <Save className="h-4 w-4 mr-1" />
                  )}
                  Save Answer
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Skipped questions info */}
      {skippedIds.size > 0 && (
        <p className="text-sm text-muted-foreground text-center">
          {skippedIds.size} question{skippedIds.size !== 1 ? 's' : ''} skipped
          in this session
        </p>
      )}
    </div>
  );
}
